import { Controller } from "@hotwired/stimulus"

// Soft-check whether a contact email is already assigned elsewhere.
export default class extends Controller {
  static targets = ["input", "message"]
  static values = {
    url: String,
    excludeId: Number,
    debounce: { type: Number, default: 150 },
  }

  connect() {
    this.timeout = null
    this.abortController = null
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.#abortFetch()
  }

  check() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.#performCheck(), this.debounceValue)
  }

  checkNow() {
    clearTimeout(this.timeout)
    this.#performCheck()
  }

  async #performCheck() {
    if (!this.hasInputTarget || !this.hasMessageTarget || !this.urlValue) return

    const email = this.inputTarget.value.trim()
    this.#abortFetch()

    if (email.length === 0) {
      this.#clearWarning()
      return
    }

    this.abortController = new AbortController()
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("email", email)
    if (this.hasExcludeIdValue && this.excludeIdValue) {
      url.searchParams.set("exclude_id", String(this.excludeIdValue))
    }

    try {
      const res = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) {
        this.#clearWarning()
        return
      }

      const data = await res.json()
      if (data.taken) {
        this.#showWarning(data)
      } else {
        this.#clearWarning()
      }
    } catch (e) {
      if (e.name !== "AbortError") this.#clearWarning()
    }
  }

  #showWarning(data) {
    const who = data.customer_name || "another customer"
    const contact = data.contact_name ? ` (${data.contact_name})` : ""
    this.messageTarget.textContent = `This email is already used by ${who}${contact}.`
    this.messageTarget.classList.remove("hidden")
    this.inputTarget.classList.add("input-warning")
  }

  #clearWarning() {
    this.messageTarget.textContent = ""
    this.messageTarget.classList.add("hidden")
    this.inputTarget.classList.remove("input-warning")
  }

  #abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
